import { Tab } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createUserFn } from 'api/usersApi';
import Modal from 'components/Shared/Modal/Modal';
import { ServerStateKeysEnum, useResidencesQuery, useUsersQuery } from 'features/queries';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaSave } from 'react-icons/fa';
import { ScaleLoader } from 'react-spinners';
import { IUser } from 'types/api/users';
import sendNotification from 'utils/notifications';
import { z } from 'zod';

interface INewAdminDialog {
  isOpen: boolean;
  onClose: () => void;
}

const createUserSchema = z.object({
  email: z.string().email({
    message: 'Email non valida',
  }),
});

type CreateUserSchema = z.infer<typeof createUserSchema>;

const NewAdminDialog: React.FC<INewAdminDialog> = ({ isOpen, onClose }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedResidences, setSelectedResidences] = useState<string[]>([]);

  const queryClient = useQueryClient();

  const { data: residences = [] } = useResidencesQuery();
  const { data: users = [] } = useUsersQuery();

  const onCreateUserSuccess = async (data: IUser) => {
    await queryClient.cancelQueries([ServerStateKeysEnum.USERS]);
    const previousValue = queryClient.getQueryData<IUser[]>([ServerStateKeysEnum.USERS]);
    const updatedValue = [...(previousValue || [])];
    updatedValue.push(data);

    queryClient.setQueryData([ServerStateKeysEnum.USERS], [...updatedValue, data]);
    sendNotification('Utente creato con successo', '', 'success', true, 5000);

    onClose();
  };

  const onCreateUserError = () => sendNotification("Impossibile creare l'utente", '', 'error', true, 5000);

  const { mutate: createUserMutation, isLoading } = useMutation(createUserFn, {
    onSuccess: onCreateUserSuccess,
    onError: onCreateUserError,
  });

  const { register, watch, setError, handleSubmit, formState } = useForm<CreateUserSchema>({
    resolver: zodResolver(createUserSchema),
    mode: 'all',
  });

  const { errors, isValid } = formState;
  const watchEmail = watch('email');

  const onSubmit = handleSubmit((data) => {
    const { email } = data;
    switch (selectedIndex) {
      case 0: // amm.generale
        createUserMutation({ email, isAdmin: true, adminPermissions: '*' });
        break;
      case 1: // amm. di residenza
        createUserMutation({ email, isAdmin: true, adminPermissions: selectedResidences });
        break;
    }
  });

  useEffect(() => {
    if (isValid) {
      const userAlreadyExist = users.find((user) => user.email === watchEmail);
      if (userAlreadyExist)
        setError('email', { message: "L'utente esiste già. Cercalo e selezionalo per modificarlo" });
    }
  }, [users, watchEmail, isValid, setError]);

  const handleResidenceChecked = (residence_id: string, checked: boolean) => {
    if (!checked && selectedResidences.includes(residence_id))
      setSelectedResidences(selectedResidences.filter((res_id) => res_id !== residence_id));
    if (checked && !selectedResidences.includes(residence_id))
      setSelectedResidences([...selectedResidences, residence_id]);
  };

  const modalTitle = (
    <div className="w-full flex flex-row items-center justify-between">Crea utente amministratore</div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
      <div className="p-4 flex flex-col justify-between items-start gap-2 w-full">
        <div className="w-full">
          <label className="form-label" htmlFor="residence.name">
            Email
          </label>

          <input {...register('email')} className="form-input" type="email" />
          {errors.email && <p className="input-error">{errors.email.message}</p>}
        </div>
        <div className="w-full">
          <label className="form-label" htmlFor="residence.emails">
            Tiplogia utente
          </label>
        </div>
        <div className="w-full">
          <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
            <Tab.List className="flex  space-x-1 bg-white border rounded-xl">
              {['Amm. generale', 'Amm. di residenza'].map((name, idx) => (
                <Tab
                  key={idx}
                  className={({ selected }) =>
                    `w-full py-2 text-sm leading-5 font-medium rounded-lg
                              focus:outline-none focus:ring-2 ring-offset-2 ring-offset-blue-400 ring-white ring-opacity-60
                              ${
                                selected
                                  ? 'bg-bluemazza text-white'
                                  : 'text-bluemazza bg-white hover:border hover:border-bluemazza'
                              }`
                  }
                >
                  {name}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels className="mt-2">
              <Tab.Panel className="bg-white p-1">
                <p className="text-md font-medium underline">
                  L&#8203;utente può gestire tutti i bandi ed i rispettivi utenti.
                </p>
              </Tab.Panel>
              <Tab.Panel className="bg-white p-1">
                <div className="w-full">
                  <label className="form-label" htmlFor="passwordConfirmation">
                    Bandi associati
                  </label>
                  {residences.map((residence, idx) => {
                    const isChecked = selectedResidences.includes(residence._id);
                    return (
                      <div key={idx} className="flex flex-row gap-4 items-center">
                        <input
                          onChange={(e) => handleResidenceChecked(residence._id, e.target.checked)}
                          checked={isChecked}
                          type="checkbox"
                          className="form-input"
                        />
                        <p className="font-bold text-md">{residence.name}</p>
                      </div>
                    );
                  })}
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>

        <div className="w-full flex flex-row items-center justify-center mt-4">
          <button
            disabled={isLoading || !isValid}
            onClick={onSubmit}
            className="bluemazza-button flex flex-row items-center gap-4"
            type="button"
          >
            {isLoading ? (
              <ScaleLoader color="blue" />
            ) : (
              <>
                {' '}
                <FaSave />
                <p>Salva</p>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default NewAdminDialog;
